<!-- JS -->
<script setup>
	import Select2 from "vue3-select2-component";
	import useHelpers from "@/Composables/UseHelpers";
	import {computed, ref} from "vue";

	const {$t} = useHelpers();
	const payment_gateway_model = ref('');
	const fpx_buyer_bank_model = ref('');
	const fpx_buyer_bank_code_model = ref('');
	const fpx_buyer_bank_name_model = ref('');
	const bank_transfer_type_model = ref('');
	const merchant_bank_account_model = ref('');

	const emit = defineEmits([
		'update:payment_gateway',
		'update:merchant_bank_account',
		'update:bank_transfer_type',
		'update:bank_transfer_date',
		'update:bank_transfer_notes',
		'update:proof_of_payment',
		'update:fpx_buyer_bank',
		'update:fpx_buyer_bank_code',
		'update:fpx_buyer_bank_name',
	]);

	const props = defineProps({
		payment_channels: {
			required: true,
			type: Object,
			default: []
		},
		payment_gateway: {
			required: false,
			default: 1
		},
		bank_transfer_type: {
			// required: true,
		},
		bank_transfer_date: {
			// required: true,
		},
		bank_transfer_notes: {
			// required: true,
		},
		proof_of_payment: {
			required: true,
		},
		merchant_bank_account: {
			// required: true,
		},
		processing: {
			required: true,
		},
		mandate_enabled: {
			required: true,
		},
		paymentGateways: {
			required: true,
		},
		fpx_gateway_code: {
			required: true,
		},
		fpx_buyer_bank: {
			required: true,
		},
		tenant: {
			required: true,
		},
		form_code: {
			required: true,
		},
		manual_bank_transfer_enabled: {
			required: true,
		},
		manual_bank_transfer_gateway_code: {
			required: true,
		},
		manual_bank_transfer_payment_notes: {
			required: true,
		},
		bankAccounts: {
			required: true,
		},
		fpxBanksList: {
			required: true,
		},
		fpxBanksLogo: {
			required: true,
		},
		total: {
			required: true,
		},
		demo_mode: {
			required: true,
		},
		errors: {
			required: true,
		},
	})

	const fpxBanksListOpts = computed(() => {
		return props.fpxBanksList.map(function (obj) {
			return {
				id: JSON.stringify(obj),
				text: obj.bankDisplayName + (obj.bankAvailability ? '' : ' (Offline)'),
				disabled: !obj.bankAvailability
			};
		})
	})

	function renderManualBankTransferNotes(notes, data) {
		let regex = new RegExp('#(' + Object.keys(data).join('|') + ')#', 'g');

		return notes.replace(regex, (m, $1) => data[$1] || m);
	}

	function setBankFpxDetails() {
		let fpxBuyerBank = JSON.parse(fpx_buyer_bank_model.value);

		fpx_buyer_bank_code_model.value = fpxBuyerBank.bank_code_hashed;
		fpx_buyer_bank_name_model.value = fpxBuyerBank.bank_display_name;

		emit('update:fpx_buyer_bank', fpx_buyer_bank_model);
		emit('update:fpx_buyer_bank_code',fpx_buyer_bank_code_model);
		emit('update:fpx_buyer_bank_name',fpx_buyer_bank_name_model);
	}

	function formatBankOption(bank) {
		if (!bank.id) { return bank.text; }
		let logo = props.fpxBanksLogo[bank.text.replace(' (Offline)', '')];

		return $('<span><img class="mr-3" style="display:inline-block;height:auto;width:48px;" src="' + logo + '" /> ' + bank.text + '</span>');
	}
</script>

<template>
	<div class="row mb-3">

		<!-- Step 3 -->
		<div class="col-12">
			<h4 class="mb-3" v-show="! processing">
				<div class="badge badge-danger step">
					{{ $t('donation-form.step') }} 3
				</div>

				{{ $t('donation-form.payment_methods') }}
			</h4>
		</div>

		<!-- FPX & MBT -->
		<div class="col-12 align-items-center" v-if="! mandate_enabled">
        <template v-for="paymentGateway in Object.values(paymentGateways)">

				<!-- Label -->
				<div class="custom-control custom-radio btn border text-left" style="padding:10px 10px 8px 40px;margin-bottom:10px;" :class="{ 'btn-light': payment_gateway == paymentGateway.code, 'btn-white': payment_gateway != paymentGateway.code }">
					<div>
						<input
							type="radio"
							:id="'payment_method_' + paymentGateway.code"
							name="payment_gateway"
							class="custom-control-input"
							:checked="payment_gateway === paymentGateway.code"
							:value="paymentGateway.code"
							v-model="payment_gateway_model"
							@input="emit('update:payment_gateway', $event.target.value)"
						/>
						<label class="custom-control-label w-100 d-flex justify-content-between" :for="'payment_method_' + paymentGateway.code">
							<span class="text-14 text-bold-500">
								{{ paymentGateway.label }}
							</span>
              <img :src="paymentGateway.logo" :alt="paymentGateway.label" :id="'payment_method_' + paymentGateway.code" class="payment-gateway-logo">
						</label>
					</div>
				</div>

				<div v-if="payment_gateway == paymentGateway.code">

					<!-- FPX -->
					<div v-if="payment_gateway != manual_bank_transfer_gateway_code">
						<Select2
							v-if="tenant.custom_fpx_payment_flow_enabled && payment_gateway == fpx_gateway_code"
							class="mb-2"
							:placeholder="$t('donation-form.select_bank')"
							:options="fpxBanksListOpts"
							:settings="{
							width:'100%',
							templateResult: formatBankOption,
							templateSelection: formatBankOption
							}"
							v-model="fpx_buyer_bank_model"
							@select="setBankFpxDetails"
						/>
                        <div class="ml-2 mt-3 mb-3">
	                        {{ paymentGateway.description }}
                        </div>

						<div class="mb-2" v-if="manual_bank_transfer_enabled"></div>
					</div>

					<!-- Manual Bank Transfer -->
					<div v-if="payment_gateway == manual_bank_transfer_gateway_code">
						<div class="mt-3">

							<!-- Notes -->
							<div
								v-if="manual_bank_transfer_payment_notes"
								class="p-3 mb-4 alert-danger text-14 mobile-mb-0 bayarcash-mbt-notes"
								v-html="renderManualBankTransferNotes(manual_bank_transfer_payment_notes, {
								organisation_name: tenant.name,
								organisation_owner: tenant.owner,
								organisation_email: tenant.email,
								organisation_phone_no: tenant.office_no || tenant.hp_no,
								form_code: form_code,
								amount: vueNumberFormat(total)
								})"
							></div>

							<!-- Bank Account -->
							<div class="form-group row mb-2 mobile-mb-0">
								<label class="col-4 col-form-label mt-2" for="merchant_bank_account">
									{{ $t('donation-form.bank_account') }}

									<!-- Required -->
									<span class="text-red"> *</span>
								</label>
								<div class="col-8 d-flex flex-column">
									<template v-for="account in bankAccounts">
										<div class="custom-control custom-radio custom-control-inline alert alert-warning w-100" style="padding-left: 35px">
											<input
												type="radio"
												class="custom-control-input"
												name="merchant_bank_account"
												:id="'merchant_bank_account_' + account.id"
												:checked="merchant_bank_account === account"
												:value="JSON.stringify(account)"
												v-model="merchant_bank_account_model"
												@input="emit('update:merchant_bank_account', $event.target.value)"
											/>
											<label class="custom-control-label mb-0 text-14 w-100 mobile-mb-0 bayarcash-mbt-notes-span" :for="'merchant_bank_account_' + account.id">
												<span>
													<img class="mb-2 bayarcash-mbt-logo-bank" :src="'/images/payments/banks/'+account.bank.toLowerCase().replaceAll(' ', '-')+'.png'">
												</span>
												<span>
													<b>Bank:</b> {{ account.bank }}
												</span>
												<span>
													<b>Pemegang Akaun:</b> {{ account.account_name }}
												</span>
												<span>
													<b>Nombor Akaun:</b> {{ account.account_no }}
												</span>
												<span>
													<b>Jumlah:</b> {{ vueNumberFormat(total) }}
												</span>
												<span class="mt-3">
													<a target="online-banking" :href="account.bank_website">
														{{ $t('donation-form.open_online_banking') }}
														<i class="fad fa-external-link ml-1"></i>
													</a>
												</span>
											</label>
										</div>
									</template>

									<!-- Error message -->
									<div class="invalid-feedback d-block" v-if="errors.merchant_bank_account">
										{{ errors.merchant_bank_account }}
									</div>
								</div>
							</div>

							<!-- Bank Transfer Type -->
							<div class="form-group row mb-2 mobile-mb-0">
								<label class="col-4 col-form-label mt-2" for="internet-banking">
									{{ $t('donation-form.bank_transfer_type') }}

									<!-- Required -->
									<span class="text-red"> *</span>
								</label>
								<div class="col-8">
									<div class="form-check pl-0">
										<div class="row">

											<!-- Online banking -->
											<div class="col-6 pr-1 mobile-pr-reset">
												<div class="custom-control custom-radio alert alert-info mobile-mb-1" style="padding-left: 35px">
													<input
														type="radio"
														name="bank_transfer_type"
														id="internet-banking"
														class="custom-control-input"
														value="Internet Banking"
														:checked="bank_transfer_type_model === 'Internet Banking'"
														v-model="bank_transfer_type_model"
														@input="emit('update:bank_transfer_type', $event.target.value)"
													>
													<label class="custom-control-label mb-0 text-14 w-100" for="internet-banking">
														Online Internet Banking
													</label>
												</div>
											</div>

											<!-- CDM -->
											<div class="col-6 pl-1 mobile-pr-reset">
												<div class="custom-control custom-radio alert alert-info" style="padding-left: 35px">
													<input
														type="radio"
														name="bank_transfer_type"
														id="cash-deposit-machine"
														class="custom-control-input"
														value="Cash Deposit Machine (CDM)"
														:checked="bank_transfer_type_model === 'Cash Deposit Machine (CDM)'"
														v-model="bank_transfer_type_model"
														@input="emit('update:bank_transfer_type', $event.target.value)"
													>
													<label class="custom-control-label mb-0 text-14 w-100" for="cash-deposit-machine">
														Cash Deposit Machine
													</label>
												</div>
											</div>
										</div>
									</div>

									<!-- Error message -->
									<div class="invalid-feedback d-block" v-if="errors.bank_transfer_type">
										{{ errors.bank_transfer_type }}
									</div>
								</div>
							</div>

							<!-- Bank Transfer Date -->
							<div class="form-group row mb-2">
								<label class="col-4 col-form-label" for="bank_transfer_date">
									{{ $t('donation-form.bank_transfer_date') }}

									<!-- Required -->
									<span class="text-red"> *</span>
								</label>
								<div class="col-8">
									<input
										type="date"
										id="bank_transfer_date"
										name="bank_transfer_date"
										required="required"
										:class="{ 'form-control': true, 'is-invalid': errors.bank_transfer_date }"
										:value="bank_transfer_date"
										@input="emit('update:bank_transfer_date', $event.target.value)"
									>

									<!-- Error message -->
									<div class="invalid-feedback" v-if="errors.bank_transfer_date">
										{{ errors.bank_transfer_date }}
									</div>
								</div>
							</div>

							<!-- Bank Transfer Notes -->
							<div class="form-group row mb-2">
								<label class="col-4 col-form-label" for="bank_transfer_notes">
									{{ $t('donation-form.bank_transfer_notes') }}
								</label>
								<div class="col-8">
									<textarea
										type="text"
										id="bank_transfer_notes"
										name="bank_transfer_notes"
										required="required"
										:class="{ 'form-control': true, 'is-invalid': errors.bank_transfer_notes }"
										:value="bank_transfer_notes"
										@input="emit('update:bank_transfer_notes', $event.target.value)"
									></textarea>

									<!-- Error message -->
									<div class="invalid-feedback" v-if="errors.bank_transfer_notes">
										{{ errors.bank_transfer_notes }}
									</div>
								</div>
							</div>

							<!-- Proof of Payment -->
							<div class="form-group row mb-2">
								<label class="col-4 col-form-label" for="proof_of_payment">
									{{ $t('donation-form.proof_of_payment') }}

									<!-- Required -->
									<span class="text-red"> *</span>
								</label>
								<div class="col-8">
									<input
										type="file"
										id="proof_of_payment"
										name="proof_of_payment"
										required="required"
										class="form-control border-0 pl-0"
										:class="{ 'is-invalid': errors.proof_of_payment }"
										@input="emit('update:proof_of_payment', $event.target.files[0])"
									>

									<!-- Error message -->
									<div class="invalid-feedback" v-if="errors.proof_of_payment">
										{{ errors.proof_of_payment }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</div>

		<!-- Direct Debit e-Mandate -->
		<div class="col-12" v-if="mandate_enabled">
			<img class="w-100 mt-2 mb-2" src="/images/payments/directdebit-participating-bank.png">
		</div>
	</div>
</template>

<style scoped>
	.payment-gateway-logo {
	  height: 24px;
	}
</style>
